import React from 'react'
import { Box, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'

export type MediaAlign = 'left' | 'center' | 'right'

/** CSS `justify-content` / `align-items` value for a media alignment setting. */
export const mediaAlignToFlex = (align: string | undefined): 'flex-start' | 'center' | 'flex-end' => {
  if (align === 'left') return 'flex-start'
  if (align === 'right') return 'flex-end'
  return 'center'
}

interface MediaLayoutControlsProps {
  align: MediaAlign
  onAlignChange: (align: MediaAlign) => void
  /** Omit to hide the Max Width control entirely (e.g. the image widget, which
   *  always fills its cell — #1069). Provide with `onMaxWidthChange` to show it
   *  (e.g. the video widget — #1071). */
  maxWidth?: number
  onMaxWidthChange?: (maxWidth: number) => void
  maxWidthOptions?: number[]
}

/**
 * Shared alignment (+ optional max-width) controls for media widgets.
 * Built once and used by both the Image widget editor (#1069) and the Video
 * widget editor (#1071) per the reuse decision recorded on #1071.
 */
export const MediaLayoutControls: React.FC<MediaLayoutControlsProps> = ({
  align,
  onAlignChange,
  maxWidth,
  onMaxWidthChange,
  maxWidthOptions = [60, 80, 100],
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="overline" color="text.secondary">Alignment</Typography>
        <ToggleButtonGroup
          value={align}
          exclusive
          onChange={(_e, val: MediaAlign | null) => val && onAlignChange(val)}
          size="small"
        >
          <ToggleButton value="left" aria-label="Align left">
            <FormatAlignLeftIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="center" aria-label="Align center">
            <FormatAlignCenterIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="right" aria-label="Align right">
            <FormatAlignRightIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {maxWidth !== undefined && onMaxWidthChange && (
        <Box>
          <Typography variant="overline" color="text.secondary">Max Width</Typography>
          <ToggleButtonGroup
            value={maxWidth}
            exclusive
            onChange={(_e, val: number | null) => val && onMaxWidthChange(val)}
            size="small"
          >
            {maxWidthOptions.map((w) => (
              <ToggleButton key={w} value={w}>{w}%</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
    </Box>
  )
}

export default MediaLayoutControls
